import { ToolUseChirpContentBlockTypename, ServerToolUseChirpContentBlockTypename, ToolResultChirpContentBlockTypename, ImageToolResultChirpContentBlockTypename, PendingToolResultChirpContentBlockTypename, DisplayOnlyChirpContentBlockTypename } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import { getToolUseIdForResult } from './chatHelpers';
import { normalizeBlock } from './normalizeBlock';
import { parseRichContentCitations } from './citationUtils';
export function isToolResultContent(block) {
  return block.__typename === ToolResultChirpContentBlockTypename || block.__typename === ImageToolResultChirpContentBlockTypename || block.__typename === PendingToolResultChirpContentBlockTypename;
}
export function isToolUseBlock(block) {
  return block.__typename === ToolUseChirpContentBlockTypename || block.__typename === ServerToolUseChirpContentBlockTypename;
}
export function isDisplayOnlyBlock(block) {
  return block.__typename === DisplayOnlyChirpContentBlockTypename;
}
function normalizeToolInput(input) {
  if (typeof input !== 'object' || input === null) return input;
  const typename = input.__typename;
  return typeof typename === 'string' && typename.startsWith('com.hubspot.chirp.ext.models.') ? convertFieldValueAvroToJson(input) : input;
}
export function buildCompanionMap(resultMsg) {
  const companionMap = new Map();
  if (!resultMsg) return companionMap;
  let currentToolUseId = null;
  for (const block of resultMsg.content) {
    if (isToolResultContent(block)) {
      currentToolUseId = block.toolUseId;
    } else if (isDisplayOnlyBlock(block) && currentToolUseId !== null && block.citationId) {
      var _companionMap$get;
      const companions = (_companionMap$get = companionMap.get(currentToolUseId)) !== null && _companionMap$get !== void 0 ? _companionMap$get : [];
      companions.push(block);
      companionMap.set(currentToolUseId, companions);
    }
  }
  return companionMap;
}
export function buildToolPairs(useMsg, resultMsg, subagentMap) {
  const toolUseBlocks = useMsg.content.filter(isToolUseBlock);
  const resultMap = new Map();
  if (resultMsg) {
    for (const block of resultMsg.content) {
      if (isToolResultContent(block)) {
        resultMap.set(block.toolUseId, block);
      }
    }
  }
  return toolUseBlocks.map(toolUse => {
    const isServer = toolUse.__typename === ServerToolUseChirpContentBlockTypename;
    const pair = {
      input: {
        id: toolUse.id,
        callId: getToolUseIdForResult(toolUse),
        name: toolUse.name,
        input: normalizeToolInput(toolUse.input),
        serverInitiated: isServer
      }
    };
    const matched = resultMap.get(getToolUseIdForResult(toolUse));
    if (matched) {
      if (matched.__typename === PendingToolResultChirpContentBlockTypename) {
        pair.pending = {
          toolUseId: matched.toolUseId,
          toolName: matched.toolName
        };
      } else if (matched.__typename === ImageToolResultChirpContentBlockTypename) {
        const imgResult = matched;
        pair.output = Object.assign({
          type: 'IMAGE',
          toolUseId: imgResult.toolUseId,
          toolName: toolUse.name,
          base64Data: imgResult.base64Data,
          mimeType: imgResult.mimeType
        }, imgResult.filename !== undefined ? {
          filename: imgResult.filename
        } : {});
      } else {
        const textResult = matched;
        pair.output = {
          type: 'TEXT',
          toolUseId: textResult.toolUseId,
          toolName: toolUse.name,
          content: textResult.content,
          isError: textResult.error
        };
      }
    }
    const subagentLookupKey = getToolUseIdForResult(toolUse);
    let subMsgs = subagentMap.get(subagentLookupKey);

    // Positional fallback: OpenAI Responses API full messages may omit
    // callId from tool-use blocks, making the key-based lookup return
    // 'fc_...' instead of 'call_...'. The Nth tool-use always corresponds
    // to the Nth tool-result in the same round (guaranteed by the API).
    if (!subMsgs && subagentMap.size > 0 && resultMsg) {
      const toolUseIdx = toolUseBlocks.indexOf(toolUse);
      if (toolUseIdx >= 0) {
        const resultBlocks = resultMsg.content.filter(isToolResultContent);
        if (toolUseIdx < resultBlocks.length) {
          const positionalId = resultBlocks[toolUseIdx].toolUseId;
          const alreadyClaimed = toolUseBlocks.some(block => block !== toolUse && getToolUseIdForResult(block) === positionalId);
          if (positionalId !== subagentLookupKey && !alreadyClaimed) {
            subMsgs = subagentMap.get(positionalId);
          }
        }
      }
    }
    if (subMsgs && subMsgs.length > 0) {
      pair.subagentMessages = subMsgs;
    }
    return pair;
  });
}
export function promoteCompanionEntries(allPairs, companionMap) {
  const promotedToolUseIds = new Set();
  const promotedEntries = [];
  for (const pair of allPairs) {
    var _ref, _pair$output$toolUseI, _pair$output, _pair$pending, _pair$output2;
    const companions = companionMap.get((_ref = (_pair$output$toolUseI = (_pair$output = pair.output) === null || _pair$output === void 0 ? void 0 : _pair$output.toolUseId) !== null && _pair$output$toolUseI !== void 0 ? _pair$output$toolUseI : (_pair$pending = pair.pending) === null || _pair$pending === void 0 ? void 0 : _pair$pending.toolUseId) !== null && _ref !== void 0 ? _ref : pair.input.id);
    if (!companions || companions.length === 0) continue;
    if (((_pair$output2 = pair.output) === null || _pair$output2 === void 0 ? void 0 : _pair$output2.type) !== 'TEXT' || pair.output.isError) continue;
    const richMeta = parseRichContentCitations(pair.output.content);
    const entriesForPair = [];
    for (const companion of companions) {
      const normalized = normalizeBlock(companion);
      if ((normalized === null || normalized === void 0 ? void 0 : normalized.type) !== 'DISPLAY_ONLY') continue;
      const displayOnly = normalized;
      for (const entry of (_displayOnly$entries = displayOnly.entries) !== null && _displayOnly$entries !== void 0 ? _displayOnly$entries : []) {
        var _displayOnly$entries;
        const rich = entry.citationId ? richMeta.get(entry.citationId) : undefined;
        entriesForPair.push(Object.assign({}, entry, {
          additionalContent: rich === null || rich === void 0 ? void 0 : rich.additionalContent
        }));
      }
    }
    if (entriesForPair.length === 0) continue;
    const keepPair = pair.subagentMessages && pair.subagentMessages.length > 0 || pair.input.name === 'send_user_update';
    if (!keepPair) {
      promotedToolUseIds.add(pair.input.id);
    }
    promotedEntries.push(...entriesForPair);
  }
  return {
    promotedEntries,
    nonPromotedPairs: allPairs.filter(pair => !promotedToolUseIds.has(pair.input.id))
  };
}